<section class="card work-map" data-testid="dashboard-map">
  <header class="work-map__header">
    <h2 class="work-map__title">Your work map</h2>
  </header>

  <!-- Consolidated scope + per-scope filter block. Left column stacks the scope
       toggle above its per-scope tab row; the report-type dropdown sits to the
       right, top-aligned with the scope toggle. The tab row + dropdown ALWAYS
       render for a scope — with 0 counts even when there's no data or the
       Available Work fetch errored. Wraps on narrow widths (dropdown drops
       below). -->
  <div class="work-map__controls" data-testid="dashboard-map-controls">
    <div class="work-map__controls-left">
      <div class="work-map__scope" role="group" aria-label="Map scope">
        <button
          type="button"
          class="work-map__scope-btn"
          [class.work-map__scope-btn--active]="scope === 'offers'"
          [attr.aria-pressed]="scope === 'offers'"
          (click)="setScope('offers')"
          data-testid="dashboard-map-scope-offers"
        >
          My Offers
        </button>
        <button
          type="button"
          class="work-map__scope-btn"
          [class.work-map__scope-btn--active]="scope === 'active'"
          [attr.aria-pressed]="scope === 'active'"
          (click)="setScope('active')"
          data-testid="dashboard-map-scope-active"
        >
          My Active
        </button>
        <button
          type="button"
          class="work-map__scope-btn"
          [class.work-map__scope-btn--active]="scope === 'availableWork'"
          [attr.aria-pressed]="scope === 'availableWork'"
          (click)="setScope('availableWork')"
          data-testid="dashboard-map-scope-available-work"
        >
          Available Work
        </button>
      </div>

      @if (scope === 'active') {
        <div class="work-map__filter-tabs" role="group" aria-label="Due date filter">
          @for (facet of dueDateFacets; track facet.value) {
            <button
              type="button"
              class="work-map__filter-tab"
              [class.work-map__filter-tab--active]="projectFilter.dueDate === facet.value"
              [attr.aria-pressed]="projectFilter.dueDate === facet.value"
              [attr.data-testid]="'dashboard-map-due-date-' + facet.value"
              (click)="setDueDateFilter(facet.value)"
            >
              {{ facet.label }}
              <span class="work-map__filter-count">{{ facet.count }}</span>
            </button>
          }
        </div>
      } @else if (scope === 'offers') {
        <div class="work-map__filter-tabs" role="group" aria-label="Work type filter">
          @for (facet of offersFacets; track facet.value) {
            <button
              type="button"
              class="work-map__filter-tab"
              [class.work-map__filter-tab--active]="projectFilter.offers === facet.value"
              [attr.aria-pressed]="projectFilter.offers === facet.value"
              [attr.data-testid]="'dashboard-map-offers-' + facet.value"
              (click)="setOffersFilter(facet.value)"
            >
              {{ facet.label }}
              <span class="work-map__filter-count">{{ facet.count }}</span>
            </button>
          }
        </div>
      } @else {
        <!-- Available Work: the rush-driven status tabs (single-select) plus the
             milk-run toggle as a SEPARATE, independent control — it composes
             with the status tabs and the report-type dropdown rather than being
             a fourth tab in this group. No work-type axis exists here. -->
        <div class="work-map__filter-tabs" role="group" aria-label="Staffing status filter">
          @for (facet of availableWorkStatusFacets; track facet.value) {
            <button
              type="button"
              class="work-map__filter-tab"
              [class.work-map__filter-tab--active]="availableWorkFilter.status === facet.value"
              [attr.aria-pressed]="availableWorkFilter.status === facet.value"
              [attr.data-testid]="'dashboard-map-available-work-' + facet.value"
              (click)="setAvailableWorkStatusFilter(facet.value)"
            >
              {{ facet.label }}
              <span class="work-map__filter-count">{{ facet.count }}</span>
            </button>
          }
        </div>
        <div class="work-map__filter-tabs" role="group" aria-label="Milk run filter">
          <button
            type="button"
            class="work-map__filter-tab"
            [class.work-map__filter-tab--active]="availableWorkFilter.milkRunOnly"
            [attr.aria-pressed]="availableWorkFilter.milkRunOnly"
            data-testid="dashboard-map-milk-run-only"
            (click)="toggleMilkRunOnly()"
          >
            Milk Run Only
            <span class="work-map__filter-count">{{ availableWorkMilkRunCount }}</span>
          </button>
        </div>
      }
    </div>

    <!-- The report-type dropdown drives ALL THREE scopes — its options are
         seeded from whichever set is in scope (the vendor's own products, or the
         Available Work result set), and it renders with just "All service types"
         when that set is empty or the fetch failed. -->
    <label class="work-map__filter-field">
      <span class="work-map__filter-label">Service Type</span>
      <select
        class="form-select work-map__filter-select"
        data-testid="dashboard-map-report-type"
        [value]="activeReportType"
        (change)="onReportTypeFilterChange($any($event.target).value)"
      >
        <option value="all">All service types</option>
        @for (option of reportTypeOptions; track option) {
          <option [value]="option">{{ option }}</option>
        }
      </select>
    </label>
  </div>

  <div
    class="work-map__body"
    [class.work-map__body--split]="mapEnabled"
    [class.work-map__body--list-only]="!mapEnabled"
  >
    @if (mapEnabled) {
      <div class="work-map__canvas">
        @if (mapsReady) {
          <google-map
            height="100%"
            width="100%"
            [center]="center"
            [zoom]="zoom"
            [options]="mapOptions"
            (mapInitialized)="onMapInitialized($event)"
          >
            @if (infoWindowPosition && selectedRow) {
              <map-info-window
                [position]="infoWindowPosition"
                (closeclick)="closeInfoWindow()"
              >
                <div class="work-map__infowindow">
                  <p class="work-map__infowindow-title">
                    {{ selectedRow.title }}
                    @if (selectedRow.streetAddress) {
                      <span class="work-map__infowindow-addr">
                        · {{ selectedRow.streetAddress }}</span
                      >
                    }
                  </p>
                  @if (selectedRow.cityStateZip) {
                    <p class="work-map__infowindow-sub">{{ selectedRow.cityStateZip }}</p>
                  }
                  <!-- The InfoWindow is information only on Available Work: a
                       need has no project, no proposal and no workspace, so
                       there is nowhere to navigate to. NOT rendered disabled —
                       a disabled button implies a destination that unlocks on
                       some condition, and there is none. The pin click has
                       already scrolled the paired card into view, and that card
                       carries Express Interest, so the vendor still reaches the
                       action in one click. Gated on the row's `kind`, not on
                       `scope`: the discriminator is the fact that decides it,
                       and the two project scopes keep this button unchanged. -->
                  @if (selectedRow.kind === 'project') {
                    <button
                      type="button"
                      class="work-map__infowindow-btn"
                      data-testid="dashboard-map-infowindow-view"
                      (click)="viewProject()"
                    >
                      View Project →
                    </button>
                  }
                </div>
              </map-info-window>
            }
          </google-map>
        } @else {
          <div class="work-map__canvas-loading">
            <p>Loading map…</p>
          </div>
        }
      </div>
    }

    <!-- ONE list for all three scopes. The dot, title, street address and
         city/state/zip come off the shared row view-model base, so only the card
         BODY below consults the `kind` discriminator. -->
    <ul class="work-map__list" data-testid="dashboard-map-list">
      @for (view of rows; track trackByRow($index, view); let i = $index) {
        <li
          #rowEl
          class="work-map__row"
          [class.work-map__row--selected]="i === selectedIndex"
          role="button"
          tabindex="0"
          [attr.data-testid]="rowTestIds[i]"
          (click)="onRowClick(i)"
          (keydown.enter)="onRowClick(i)"
        >
          <span
            class="work-map__row-dot"
            [style.background]="view.color"
            aria-hidden="true"
          ></span>
          <span class="work-map__row-text">
            <!-- Available Work leads with its status label, so the dot and the
                 word it means read as one unit above the property name. The
                 project scopes keep their status/due line at the foot of the
                 card, where their two legends have always put it. -->
            @if (view.kind === 'availableWork') {
              <span class="work-map__row-status">{{ view.statusLabel }}</span>
            }
            <span class="work-map__row-title">
              {{ view.title }}
              @if (view.streetAddress) {
                <span class="work-map__row-addr"> · {{ view.streetAddress }}</span>
              }
            </span>
            <span class="work-map__row-sub">{{ view.cityStateZip }}</span>

            @if (view.kind === 'project') {
              @if (scope === 'offers') {
                <span class="work-map__row-meta">
                  <span
                    class="pill"
                    [class.pill--active]="view.workType === 'workAssignment'"
                    [class.pill--invitation]="view.workType === 'quoteRequest'"
                    [attr.data-testid]="rowTestIds[i] + '-work-type'"
                  >
                    {{ view.workTypeLabel }}
                  </span>
                </span>
              }
              <span class="work-map__row-due">
                @if (scope === 'active') {
                  {{ rowDueLabel(view) }}
                  @if (view.row.dueDate) {
                    — {{ view.row.dueDate | date : 'MM/dd/yyyy' }}
                  }
                } @else {
                  {{ rowStatusLabel(view) }}
                }
              </span>
            } @else {
              <!-- Property facts on ONE muted line, not a chip row — and only
                   when at least one of the three is present (all are nullable),
                   so the card never shows an empty line or a stray separator. -->
              @if (view.metadataLine) {
                <span
                  class="work-map__row-facts"
                  [attr.data-testid]="rowTestIds[i] + '-facts'"
                >
                  {{ view.metadataLine }}
                </span>
              }
              <span class="work-map__row-meta">
                @if (view.reportType) {
                  <span
                    class="pill pill--info"
                    [attr.data-testid]="rowTestIds[i] + '-report-type'"
                  >
                    {{ view.reportType }}
                  </span>
                }
                @if (view.need.feeAmount != null) {
                  <!-- "estimated" sits immediately beside the number, never in a
                       tooltip: this is the PA estimate, and a bare figure would
                       read as a firm offer. -->
                  <span
                    class="work-map__row-fee"
                    [attr.data-testid]="rowTestIds[i] + '-fee'"
                  >
                    {{ view.need.feeAmount | currency : 'USD' : 'symbol' : '1.0-0' }}
                    <span class="work-map__row-fee-note">estimated</span>
                  </span>
                }
                @if (view.isMilkRun) {
                  <!-- The badge carries its distance — that number is what makes
                       a vendor decide to bundle the trip. WHICH need it is near
                       rides along as hover text rather than inline: this card
                       already carries a status dot, a report-type badge, a facts
                       line and a fee, and a fifth piece of inline text would
                       cost more than the answer is worth to most readers. -->
                  <span
                    class="pill pill--success"
                    [attr.data-testid]="rowTestIds[i] + '-milk-run'"
                    [attr.title]="view.milkRunTooltip || null"
                  >
                    Milk run · {{ view.closestNeedDistanceLabel }}
                  </span>
                }
              </span>
              @if (!view.position) {
                <!-- A coordinate-less need lists and counts like any other row;
                     say so quietly so a vendor comparing list against map isn't
                     left wondering why this card has no pin. -->
                <span
                  class="work-map__row-off-map"
                  [attr.data-testid]="rowTestIds[i] + '-not-on-map'"
                >
                  Not on map
                </span>
              }

              <!-- The action. Every interest mutation lives here and nowhere
                   else (the InfoWindow deliberately carries none), which is what
                   keeps the in-flight guard and the commit-after-resolve rule in
                   one place. Both buttons stop the click from reaching the row's
                   own (click), which would otherwise zoom the map and open the
                   InfoWindow behind the action. -->
              <span class="work-map__row-action">
                @if (view.need.expressedInterest) {
                  <span
                    class="work-map__row-interest"
                    [attr.data-testid]="rowTestIds[i] + '-interest-sent'"
                  >
                    interest sent
                    @if (view.need.expressedOn) {
                      {{ view.need.expressedOn | date : 'MMM d' }}
                    }
                  </span>
                  <button
                    type="button"
                    class="btn btn--link work-map__row-btn"
                    [disabled]="isInterestPending(view)"
                    [attr.data-testid]="rowTestIds[i] + '-withdraw'"
                    (click)="withdrawInterest(view, $event)"
                  >
                    Withdraw
                  </button>
                } @else {
                  <button
                    type="button"
                    class="btn btn--secondary work-map__row-btn"
                    [disabled]="isInterestPending(view)"
                    [attr.data-testid]="rowTestIds[i] + '-express'"
                    (click)="expressInterest(view, $event)"
                  >
                    Express Interest
                  </button>
                }
              </span>
            }
          </span>
        </li>
      } @empty {
        <!-- An empty row set has FOUR different causes on Available Work, and
             they need four different answers. Precedence, and why:
               1. loading  — an in-flight fetch is not an empty result.
               2. failed   — say the fetch failed and show the server's own
                             reason; the filter controls above stay rendered.
               3. no enabled services — the ONE actionable case. The vendor
                             qualifies for nothing, so "no work available" would
                             be a true sentence pointing at the wrong problem.
                             Gated on an UNFILTERED count of 0 as well: a vendor
                             whose feed has rows but whose filters cut them to
                             zero is not looking at a services problem, whatever
                             their cached profile says, and pointing them at
                             Manage Services would be plainly false.
               4. genuine empty — qualified, nothing open right now.
             The scope gate comes FIRST so none of that copy can leak onto the
             two project scopes, which keep their one existing message. -->
        @if (scope === 'availableWork') {
          @if (availableWorkLoading) {
            <li
              class="work-map__loading"
              data-testid="dashboard-map-available-work-loading"
              aria-live="polite"
            >
              <span class="work-map__spinner" aria-hidden="true"></span>
              <span>Loading available work…</span>
            </li>
          } @else if (availableWorkError) {
            <li
              class="work-map__empty work-map__empty--error"
              data-testid="dashboard-map-available-work-error"
              aria-live="polite"
            >
              <span class="work-map__empty-title">
                We couldn't load available work.
              </span>
              <!-- The envelope's own message, rendered verbatim: it is
                   server-authored copy (that is what ApiFailureError marks) and
                   it is the only thing on screen that says WHY. -->
              <span class="work-map__empty-detail">{{ availableWorkError }}</span>
            </li>
          } @else if (!availableWorkHasEnabledServices && availableWorkTotalCount === 0) {
            <li
              class="work-map__empty work-map__empty--action"
              data-testid="dashboard-map-available-work-no-services"
            >
              <span class="work-map__empty-title">
                You don't have any enabled services yet.
              </span>
              <span class="work-map__empty-detail">
                Available work is matched to the services you're enabled for, so
                there's nothing to show until at least one is enabled.
              </span>
              <button
                type="button"
                class="btn btn--secondary work-map__empty-btn"
                data-testid="dashboard-map-available-work-manage-services"
                (click)="goToServices()"
              >
                Manage Services
              </button>
            </li>
          } @else {
            <!-- The genuine empty. One state, two wordings: with rows in the
                 feed but none surviving the filters, blaming the vendor's
                 services would be plainly false. `availableWorkTotalCount` is
                 the UNFILTERED count, so it tells those two apart. -->
            <li class="work-map__empty" data-testid="dashboard-map-empty">
              @if (availableWorkTotalCount) {
                No available work matches these filters.
              } @else {
                No available work matches your services right now.
              }
            </li>
          }
        } @else {
          <li class="work-map__empty" data-testid="dashboard-map-empty">
            No sites in this view.
          </li>
        }
      }
    </ul>
  </div>

  <div class="work-map__legend" data-testid="dashboard-map-legend">
    @for (entry of legend; track entry.label) {
      <span class="work-map__legend-item">
        <span
          class="work-map__legend-dot"
          [style.background]="entry.color"
          aria-hidden="true"
        ></span>
        {{ entry.label }}
      </span>
    }
  </div>
</section>
